/** Reply coach panel for one merchant reply: tags, style, click-to-use drafts, edit + send from Gmail, book a call. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarPlus, Loader2, RefreshCw, Send, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Tags } from './CoachTags';

function nextSlot() {
  const d = new Date(); d.setDate(d.getDate() + (d.getDay() === 5 ? 3 : d.getDay() === 6 ? 2 : 1)); d.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T10:00`;
}

function BookIt({ gmailId, booked, onBooked }) {
  const [at, setAt] = useState(nextSlot());
  const [mins, setMins] = useState(30);
  const [busy, setBusy] = useState(false);
  if (booked) return <a href={booked.html_link} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 underline" data-testid="coach-booked-link">Call booked — open in Google Calendar</a>;
  const go = async () => {
    setBusy(true);
    try { const ev = await api.request(`/reply-coach/${gmailId}/book`, { method: 'POST', body: JSON.stringify({ start: new Date(at).toISOString(), duration_minutes: mins }) }); toast.success('Invite sent from your Google Calendar'); onBooked(ev); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed p-3" data-testid="coach-book">
      <CalendarPlus className="h-4 w-4 text-primary" /><span className="text-sm font-medium">Book it</span>
      <Input type="datetime-local" className="h-8 w-52 text-xs" value={at} onChange={(e) => setAt(e.target.value)} data-testid="coach-book-time" />
      <select className="h-8 rounded-md border bg-background px-2 text-xs" value={mins} onChange={(e) => setMins(Number(e.target.value))} data-testid="coach-book-duration">
        {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min</option>)}</select>
      <Button size="sm" variant="outline" disabled={busy} onClick={go} data-testid="coach-book-btn">{busy ? 'Booking…' : 'Send invite'}</Button>
    </div>
  );
}

function Drafts({ drafts, onPick }) {
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="coach-drafts">
      {drafts.map((d, i) => (
        <button key={d.label + i} type="button" onClick={() => onPick(d.text)} data-testid={`coach-draft-${i}`}
          className="group flex flex-col items-stretch justify-start rounded-xl border bg-card p-4 text-left text-sm transition-colors hover:border-primary hover:bg-primary/5">
          <span className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.label}<span className="normal-case tracking-normal text-primary opacity-0 transition-opacity group-hover:opacity-100">Click to use</span></span>
          <span className="whitespace-pre-line leading-relaxed">{d.text}</span>
        </button>))}
    </div>
  );
}

export function CoachPanel({ gmailId, onChanged }) {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState('');
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => { setV(null); setErr(''); setText(''); api.request(`/reply-coach/${gmailId}`).then(setV).catch((e) => setErr(e.message || 'This reply could not be opened')); }, [gmailId]);
  const run = async (kind, fn) => { setBusy(kind); try { await fn(); } catch (e) { toast.error(e.message); } finally { setBusy(''); } };
  const analyze = () => run('analyze', async () => { setV(await api.request(`/reply-coach/${gmailId}/analyze`, { method: 'POST' })); onChanged(); });
  const send = () => run('send', async () => { const r = await api.request(`/reply-coach/${gmailId}/send`, { method: 'POST', body: JSON.stringify({ text }) }); toast.success(`Sent to ${r.to} from your Gmail`); setV({ ...v, coach: { ...(v.coach || {}), sent_at: new Date().toISOString() } }); onChanged(); });
  if (err) return <p className="py-20 text-center text-sm text-muted-foreground" data-testid="coach-error">This reply isn't in your synced Gmail. ({err})</p>;
  if (!v) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  const c = v.coach;
  const m = v.message;
  return (
    <div className="space-y-5" data-testid="coach-panel">
      <div>
        <p className="text-xs text-muted-foreground">{m.from} · {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''}</p>
        <h2 className="mt-1 text-lg font-semibold" data-testid="coach-subject">{m.subject || '(no subject)'}</h2>
        {v.context.business && <p className="text-sm text-muted-foreground">{v.context.business}{v.context.stage ? ` · ${v.context.stage}` : ''}{v.context.proposal_status ? ` · proposal ${v.context.proposal_status}` : ''}</p>}
        <p className="mt-3 whitespace-pre-line rounded-lg bg-muted/50 p-4 text-sm leading-relaxed" data-testid="coach-body">{c?.body || m.snippet}</p>
      </div>
      {!c?.analysis ? (
        <Button onClick={analyze} disabled={!!busy} data-testid="coach-analyze-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}Read this reply and draft a response</Button>
      ) : (<>
        <div className="space-y-2"><Tags a={c.analysis} style={c.style} /><p className="text-sm text-muted-foreground" data-testid="coach-summary">{c.analysis.summary}</p></div>
        {c.analysis.intent === 'unsubscribe' ? <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="coach-unsubscribed">They asked to stop. No reply is drafted and campaigns to this contact are stopped.</p> : (<>
          <div className="flex items-center justify-between"><p className="text-sm font-semibold">Suggested replies</p>
            <button type="button" onClick={analyze} disabled={!!busy} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="coach-regenerate"><RefreshCw className={`h-3.5 w-3.5 ${busy === 'analyze' ? 'animate-spin' : ''}`} />Write new drafts</button></div>
          <Drafts drafts={c.drafts || []} onPick={(t) => { setText(t); toast.message('Draft pasted — edit it, then send'); }} />
          <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder="Click a draft above to paste it here, then edit before sending." data-testid="coach-reply-box" />
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={send} disabled={!text.trim() || !!busy} data-testid="coach-send-btn"><Send className="mr-2 h-4 w-4" />{busy === 'send' ? 'Sending…' : 'Send from my Gmail'}</Button>
            {c.sent_at && <span className="text-xs text-emerald-700" data-testid="coach-sent-at">Replied {new Date(c.sent_at).toLocaleString()}</span>}
          </div>
          <BookIt gmailId={gmailId} booked={c.event} onBooked={(ev) => setV({ ...v, coach: { ...c, event: ev } })} />
        </>)}
      </>)}
    </div>
  );
}
